import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';
import { Observable, Subject, firstValueFrom } from 'rxjs';

export interface ChatMessage {
    role: 'user' | 'bot';
    content: string;
    timestamp: Date;
    isStreaming?: boolean;
    isPinned?: boolean;
    id?: string;
    /**
     * Marks the localized greeting shown at the top of the conversation. It is
     * a UI string, not real content, so it is never persisted: it is rebuilt in
     * the current language on every load (see CustomChatbotComponent).
     */
    isWelcome?: boolean;
    /**
     * Set when the backend signalled a security-policy block for this response
     * (see the policy-error marker in CustomChatbotComponent). The template
     * keeps the streamed content but renders a red error banner beneath it.
     */
    isPolicyError?: boolean;
    /**
     * Set when the stream ended because the model hit its token limit rather
     * than finishing naturally. The template offers a "Continue" action while
     * this is true.
     */
    isTruncated?: boolean;
    /**
     * Set when the truncation reason was specifically the model's output token
     * limit (`max_tokens`), as opposed to any other stop reason. Lets the UI
     * explain *why* the answer stopped instead of showing a generic cut-off.
     */
    isTokenLimit?: boolean;
    /**
     * Set when the account's token allowance is exhausted (`remaining: 0` on
     * the `error` SSE event). Distinct from isTokenLimit, which is about a
     * single answer being cut short: this one means no further questions can
     * be answered at all, so the UI offers no retry or continue action.
     */
    isQuotaExhausted?: boolean;
    /** Percentage of the allowance consumed, when the backend reports it. */
    quotaPercentageUsed?: number;
    /**
     * Set when the backend reported a hard failure (validation, Bedrock
     * throttling/dependency error, etc.) via the `error` SSE event. The
     * template offers a "Retry" action while this is true.
     */
    isError?: boolean;
    /** Whether the backend flagged this failure as transient/retryable. */
    isErrorRetryable?: boolean;
}

export interface StreamChunk {
    chunk: string;
}

export interface StreamTruncated {
    sessionId: string;
    stopReason: string;
}

export interface StreamGuardrail {
    sessionId: string;
}

export interface StreamKeepalive {
    ts: number;
}

/** Allowance usage, reported on the bare data line that trails `event: end`. */
export interface StreamQuota {
    percentageUsed: number;
}

export interface StreamError {
    message: string;
    retryable: boolean;
    /**
     * Quota fields, sent only when the failure is the account's token
     * allowance running out. `remaining: 0` is what marks it as exhausted —
     * the message text itself is not something to match on.
     */
    remaining?: number;
    percentage_used?: number;
}

/**
 * Emitted per SSE frame. A `chunk` event carries streamed content; a
 * `truncated` event signals the response was cut off by the model's token
 * limit (as opposed to finishing naturally), so the UI can offer to continue.
 * A `guardrail` event signals the request was blocked by a security policy.
 * A `keepalive` event is a no-op ping the proxy sends to keep the connection
 * alive during idle gaps — it carries no meaningful payload for the UI. An
 * `error` event signals a hard backend failure; the stream ends right after.
 * A `quota` event reports how much of the token allowance is consumed; it
 * trails the `end` frame on a successful answer.
 */
export type ChatStreamEvent =
    | { type: 'chunk'; chunk: string }
    | { type: 'truncated'; stopReason: string }
    | { type: 'guardrail'; sessionId: string }
    | { type: 'keepalive'; ts: number }
    | { type: 'error'; message: string; retryable: boolean }
    | { type: 'token_limit'; message: string; percentageUsed?: number }
    | { type: 'quota'; percentageUsed: number };

const STREAM_DATA_PREFIX = 'data: ';
const STREAM_EVENT_PREFIX = 'event: ';
const STREAM_EVENT_TRUNCATED = 'truncated';
const STREAM_EVENT_GUARDRAIL = 'guardrail';
const STREAM_EVENT_KEEPALIVE = 'keepalive';
const STREAM_EVENT_ERROR = 'error';

/**
 * Service for custom chatbot with streaming support
 */
@Injectable({ providedIn: 'root' })
export class CustomChatbotService {
    private readonly http = inject(HttpClient);
    private readonly storageService = inject(StorageService);
    private readonly apiUrl = `${environment.apiUrl}company/chatbot/ask`;
    private readonly resetUrl = `${environment.apiUrl}company/chatbot/reset`;
    /** Aborts the in-flight SSE request; see cancelStream(). */
    private streamAbortController?: AbortController;

    /**
     * Cancels an in-flight streamed answer, if any. Without this the fetch
     * reader keeps consuming the response (and the component keeps appending
     * to a message list that may already have been cleared or replaced).
     */
    cancelStream(): void {
        this.streamAbortController?.abort();
        this.streamAbortController = undefined;
    }

    /**
     * Ends a stream that stopped early. A deliberate cancel (AbortError) is a
     * normal completion, not a failure — surfacing it would show the user an
     * error for something they asked for.
     */
    private failStream(subject: Subject<ChatStreamEvent>, error: unknown): void {
        if (error instanceof DOMException && error.name === 'AbortError') {
            subject.complete();
            return;
        }
        subject.error(error);
    }

    /**
     * Reset the chatbot session on the backend.
     * Token and region rewrite are handled by the HTTP interceptors.
     */
    resetSession(): Promise<any> {
        return firstValueFrom(this.http.post(this.resetUrl, {}));
    }

    /**
     * Send a question to the chatbot and receive streaming response
     * @param question The user's question
     * @returns Observable that emits chunks of the response
     */
    askQuestion(question: string): Observable<ChatStreamEvent> {
        const responseSubject = new Subject<ChatStreamEvent>();

        if (this.simulateDevScenario(question, responseSubject)) {
            return responseSubject.asObservable();
        }

        const token = this.storageService.getToken();
        const userRegion = this.storageService.getUserRegion();

        // Build URL with region if available. Done manually here because fetch
        // (needed for SSE streaming) bypasses the Angular HTTP interceptors.
        let url = this.apiUrl;
        try {
            const urlObj = new URL(this.apiUrl);
            if (userRegion && urlObj.hostname.includes('api')) {
                urlObj.hostname = urlObj.hostname.replace('api', `${userRegion}.api`);
                url = urlObj.toString();
            }
        } catch (e) {
            console.error('Error parsing apiUrl:', e);
            if (userRegion) {
                url = url.replace('api', `${userRegion}.api`);
            }
        }

        // Build headers
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // Supersede any still-running stream, then track this one so it can be
        // cancelled (panel closed, history cleared, new question asked).
        this.cancelStream();
        const abortController = new AbortController();
        this.streamAbortController = abortController;

        fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({ question }),
            signal: abortController.signal,
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body?.getReader();
                const decoder = new TextDecoder();

                if (!reader) {
                    throw new Error('Response body is not readable');
                }

                let buffer = '';
                // SSE frame this data line belongs to; reset once consumed.
                let pendingEvent = '';

                const readStream = (): void => {
                    reader.read().then(({ done, value }) => {
                        if (done) {
                            responseSubject.complete();
                            return;
                        }

                        // Append new chunk to buffer
                        buffer += decoder.decode(value, { stream: true });

                        // Process complete lines in buffer
                        let lineBreakIndex;
                        while ((lineBreakIndex = buffer.indexOf('\n')) !== -1) {
                            const line = buffer.slice(0, lineBreakIndex).trim();
                            buffer = buffer.slice(lineBreakIndex + 1);

                            if (line.startsWith(STREAM_EVENT_PREFIX)) {
                                pendingEvent = line.substring(STREAM_EVENT_PREFIX.length).trim();
                                // Deliberately NOT completing here: the `end` frame
                                // is followed by a quota data line, which we'd never
                                // read if we closed the subject on the event line.
                                // The reader's `done` completes us instead.
                            } else if (line.startsWith(STREAM_DATA_PREFIX)) {
                                const data = line.substring(STREAM_DATA_PREFIX.length).trim();
                                const currentEvent = pendingEvent;
                                pendingEvent = '';

                                if (data) {
                                    try {
                                        if (currentEvent === STREAM_EVENT_TRUNCATED) {
                                            const parsed: StreamTruncated = JSON.parse(data);
                                            responseSubject.next({ type: 'truncated', stopReason: parsed.stopReason });
                                        } else if (currentEvent === STREAM_EVENT_GUARDRAIL) {
                                            const parsed: StreamGuardrail = JSON.parse(data);
                                            responseSubject.next({ type: 'guardrail', sessionId: parsed.sessionId });
                                        } else if (currentEvent === STREAM_EVENT_KEEPALIVE) {
                                            const parsed: StreamKeepalive = JSON.parse(data);
                                            responseSubject.next({ type: 'keepalive', ts: parsed.ts });
                                        } else if (currentEvent === STREAM_EVENT_ERROR) {
                                            const parsed: StreamError = JSON.parse(data);
                                            // An exhausted token allowance arrives on the
                                            // `error` event too, but it is not a failure to
                                            // retry — it needs its own message. Keyed on
                                            // remaining === 0, not on the message text.
                                            if (parsed.remaining === 0) {
                                                responseSubject.next({
                                                    type: 'token_limit',
                                                    message: parsed.message,
                                                    percentageUsed: parsed.percentage_used
                                                });
                                            } else {
                                                responseSubject.next({ type: 'error', message: parsed.message, retryable: parsed.retryable });
                                            }
                                        } else {
                                            const parsed: StreamChunk & StreamQuota = JSON.parse(data);
                                            if (parsed.chunk) {
                                                responseSubject.next({ type: 'chunk', chunk: parsed.chunk });
                                            } else if (typeof parsed.percentageUsed === 'number') {
                                                // Trails the `end` frame as a bare data line
                                                // (no `event:` of its own), so it lands here
                                                // rather than in a branch above.
                                                responseSubject.next({ type: 'quota', percentageUsed: parsed.percentageUsed });
                                            }
                                        }
                                    } catch (e) {
                                        console.error('Error parsing SSE data:', e, 'Data:', data);
                                    }
                                }
                            }
                        }

                        readStream();
                    }).catch(error => {
                        this.failStream(responseSubject, error);
                    });
                };

                readStream();
            })
            .catch(error => {
                this.failStream(responseSubject, error);
            });

        return responseSubject.asObservable();
    }

    /**
     * DEV-ONLY: lets you exercise the `error`/`truncated`/`guardrail`/
     * `token_limit` SSE events end-to-end (banner, standalone-vs-inline
     * placement, retry/continue actions) from the chat input without needing
     * the backend to actually fail. Type one of the magic questions below into
     * the chat. Remove this before shipping.
     *
     *   /simulate-error            hard failure, no content, retryable
     *   /simulate-error-fatal      hard failure, no content, non-retryable
     *   /simulate-error-partial    partial content, then a retryable failure
     *   /simulate-truncated        partial content, then the model's max_tokens
     *   /simulate-guardrail        blocked with no content
     *   /simulate-guardrail-partial  partial content, then blocked
     *   /simulate-quota            account allowance exhausted, no content
     *   /simulate-quota-partial    partial content, then allowance exhausted
     *   /simulate-quota-info       normal answer, then 75% used (info tier)
     *   /simulate-quota-warning    normal answer, then 85% used (warning tier)
     */
    private simulateDevScenario(question: string, subject: Subject<ChatStreamEvent>): boolean {
        // if (environment.production) {
        //     return false;
        // }

        const trigger = question.trim().toLowerCase();

        const scenarios: Record<string, () => void> = {
            // Fails immediately with no content — renders the standalone banner.
            '/simulate-error': () => {
                subject.next({ type: 'error', message: 'Simulated retryable error.', retryable: true });
                subject.complete();
            },
            // Account token allowance exhausted — renders the quota banner with
            // no retry/continue action.
            '/simulate-quota': () => {
                subject.next({ type: 'token_limit', message: 'Token limit reached', percentageUsed: 100 });
                subject.complete();
            },
            // Streams a partial answer, then the allowance runs out — the more
            // likely real-world shape, since quota usually expires mid-answer.
            '/simulate-quota-partial': () => {
                subject.next({ type: 'chunk', chunk: 'Starting to answer before the allowance ran out... ' });
                setTimeout(() => {
                    subject.next({ type: 'token_limit', message: 'Token limit reached', percentageUsed: 100 });
                    subject.complete();
                }, 400);
            },
            // Answers normally, then reports usage in the lower tier — the
            // neutral info banner should appear above the input.
            '/simulate-quota-info': () => {
                subject.next({ type: 'chunk', chunk: 'Here is a perfectly normal answer. ' });
                setTimeout(() => {
                    subject.next({ type: 'quota', percentageUsed: 75 });
                    subject.complete();
                }, 400);
            },
            // Same, but in the upper tier — the amber warning banner.
            '/simulate-quota-warning': () => {
                subject.next({ type: 'chunk', chunk: 'Here is a perfectly normal answer. ' });
                setTimeout(() => {
                    subject.next({ type: 'quota', percentageUsed: 85 });
                    subject.complete();
                }, 400);
            },
            // Non-retryable — same standalone banner, but no Retry button.
            '/simulate-error-fatal': () => {
                subject.next({ type: 'error', message: 'Simulated fatal error.', retryable: false });
                subject.complete();
            },
            // Streams some content first, then fails — renders the banner
            // inline inside the bubble, below the partial content.
            '/simulate-error-partial': () => {
                subject.next({ type: 'chunk', chunk: "Here's a partial answer before things broke... " });
                setTimeout(() => {
                    subject.next({ type: 'error', message: 'Simulated error after partial content.', retryable: true });
                    subject.complete();
                }, 400);
            },
            // Streams a partial answer, then hits the (simulated) token limit —
            // renders the amber truncated-notice with a Continue button.
            '/simulate-truncated': () => {
                subject.next({ type: 'chunk', chunk: 'Here is a long answer that got cut off right in the middle of' });
                setTimeout(() => {
                    subject.next({ type: 'truncated', stopReason: 'max_tokens' });
                    subject.complete();
                }, 400);
            },
            // Blocked with no content to show — renders the standalone
            // policy-error banner (no chat bubble).
            '/simulate-guardrail': () => {
                subject.next({ type: 'guardrail', sessionId: 'simulated-session' });
                subject.complete();
            },
            // Streams some content first, then gets blocked — renders the
            // policy-error banner inline inside the bubble.
            '/simulate-guardrail-partial': () => {
                subject.next({ type: 'chunk', chunk: 'Here is what I can tell you so far... ' });
                setTimeout(() => {
                    subject.next({ type: 'guardrail', sessionId: 'simulated-session' });
                    subject.complete();
                }, 400);
            },
        };

        const scenario = scenarios[trigger];
        if (!scenario) {
            return false;
        }

        // Small delay so the typing-dots placeholder is visible first, like a
        // real request would be.
        setTimeout(scenario, 300);
        return true;
    }
}
